import React, { FunctionComponent } from "react";
import variables from "@episerver/design-tokens/dist/json/variables";
import { Typography } from "@episerver/ui-framework";

import "./color-variables.scss";

interface ColorsType {
    name: string;
    hex: string;
    alpha: number;
}

interface Groups {
    [key: string]: string;
}

const palette: Groups = {
    primary: "Primary",
    secondary: "Secondary",
    surface: "Surface",
    success: "Success",
    reminder: "Reminder",
    warning: "Warning",
    error: "Error"
};

// Highlight colors
const onPalette: Groups = {
    "on-primary": "On Primary",
    "on-secondary": "On Secondary",
    "on-surface": "On Surface",
    "on-success": "On Success",
    "on-reminder": "On Reminder",
    "on-warning": "On Warning",
    "on-error": "On Error"
};

interface ColorMap {
    [scale: string]: {
        [weight: string]: {
            name: string;
            value: string;
        };
    };
}

const weights = ["900", "800", "700", "600", "500", "400", "300", "200", "100", "050"];

//  Color info lookup through scale (group) name and weight. I.e. colorMap["primary"][500] = { name: "primary500", hex: "#1456f1" }
const createColorMap = (variables: ColorsType[], scales: Groups): ColorMap => {
    const colorMap: ColorMap = {};

    Object.keys(scales).forEach(scale => {
        const variableName = "color-" + scale + "-";
        const colors = variables.filter(
            c => c.name.indexOf(variableName) === 0
        );

        // Make sure there's always an object to lookup up with weight
        colorMap[scale] = {};

        colors.forEach(c => {
            const weight = c.name.replace(variableName, "");

            colorMap[scale][weight] = {
                name: c.name,
                value: `#${c.hex}`
            };
        });

        // Fill in the gaps in the color map
        weights.forEach(weight => {
            if (!colorMap[scale][weight]) {
                console.error(`color-variables Error: Color scale ${scale} with weight ${weight} is missing when creating the color table.`); // eslint-disable-line no-console
                colorMap[scale][weight] = {
                    name: "Missing",
                    value: "#bada55"
                };
            }
        });
    });

    return colorMap;
};

export const ColorVariables: FunctionComponent = () => {
    const colorVariables = variables.filter(c => c.name && c.name.startsWith("color"));
    const colorMap = createColorMap(colorVariables, palette); // Usage: colorMap["primary"][500] = { name: "$color-primary-500", hex: "#1456f1" }
    const textColorMap = createColorMap(colorVariables, onPalette);
    const names = Object.keys(palette);

    return (
        <table className="epi-ds-site-table epi-ds-color-variables">
            <thead>
                <tr>
                    <th>Weight \ Color</th>
                    {names.map(name => (
                        <th key={name}>{palette[name]}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {weights.map(weight => (
                    <tr key={weight}>
                        <td>{weight}</td>
                        {names
                            .map(name => ({
                                ...colorMap[name][weight],
                                onColor: textColorMap[`on-${name}`][weight].value
                            })) // Create empty objects for missing values so the <td> is still rendered
                            .map(c => (
                                <td key={c.value} className="color-box" style={{
                                    backgroundColor: c.value,
                                    borderColor: c.value,
                                    color: c.onColor
                                }}>
                                    <Typography use="body2" tag="div">{c.value}</Typography>
                                    <Typography use="body2" tag="div">{c.name}</Typography>
                                </td>
                            ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
